<div class="space-y-6">
  <app-page-header
    title="Tarification des licences"
    subtitle="Prix de chaque palier (Bronze · Argent · Or). Modifiable à tout moment — les licences en cours conservent leur prix historique."
  />

  @if (error() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="error()"
      loadingMessage="Chargement…"
      (retry)="load()"
    />
  } @else {
    <div class="grid gap-6 lg:grid-cols-3">
      @for (tier of allTiers; track tier) {
        @let existing = items().find(p => p.tier === tier);
        <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="flex items-start justify-between">
            <div>
              <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">
                Palier {{ tierLabel[tier] }}
              </h2>
              @if (!existing) {
                <p class="mt-1 text-xs text-warning-600 dark:text-warning-400">Non configuré</p>
              }
            </div>
            <span
              class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
              [class.bg-gray-100]="tier === 'BRONZE'"
              [class.text-gray-700]="tier === 'BRONZE'"
              [class.dark:bg-gray-800]="tier === 'BRONZE'"
              [class.dark:text-gray-300]="tier === 'BRONZE'"
              [class.bg-blue-light-50]="tier === 'SILVER'"
              [class.text-blue-light-700]="tier === 'SILVER'"
              [class.dark:bg-blue-light-500/15]="tier === 'SILVER'"
              [class.dark:text-blue-light-400]="tier === 'SILVER'"
              [class.bg-warning-50]="tier === 'GOLD'"
              [class.text-warning-700]="tier === 'GOLD'"
              [class.dark:bg-warning-500/15]="tier === 'GOLD'"
              [class.dark:text-warning-400]="tier === 'GOLD'"
            >
              {{ tier }}
            </span>
          </div>

          @if (existing) {
            <dl class="mt-5 space-y-3 text-sm">
              <div class="flex items-baseline justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Prix</dt>
                <dd class="text-2xl font-bold text-gray-800 dark:text-white/90">
                  {{ existing.price | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">{{ existing.currency }}</span>
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Durée</dt>
                <dd class="text-gray-800 dark:text-white/90">
                  {{ existing.durationDays == null ? 'Illimitée' : existing.durationDays + ' jour(s)' }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Prestataires max</dt>
                <dd class="text-gray-800 dark:text-white/90">
                  {{ existing.maxPrestataires == null ? 'Illimité' : existing.maxPrestataires }}
                </dd>
              </div>
              @if (existing.description) {
                <div class="border-t border-gray-100 pt-3 dark:border-gray-800">
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ existing.description }}</p>
                </div>
              }
            </dl>
          } @else {
            <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Aucun tarif défini — les opérateurs ne peuvent pas acheter cette licence tant que ce n'est pas configuré.
            </p>
          }

          <button
            type="button"
            (click)="openEdit(tier)"
            class="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            @if (existing) { Modifier } @else { Configurer }
          </button>
        </section>
      }
    </div>
  }
</div>

<!-- Edit modal -->
<app-modal [isOpen]="editOpen()" (close)="closeEdit()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">
    Palier {{ editTier() ? tierLabel[editTier()!] : '' }}
  </h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    Le nouveau prix s'appliquera aux achats futurs. Les licences en cours conservent leur prix historique.
  </p>

  @if (editError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ editError() }}
    </div>
  }

  <form [formGroup]="editForm" (ngSubmit)="submitEdit()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Prix (XOF) *</label>
      <input type="number" step="1" min="1" formControlName="price"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
    </div>

    <!-- Durée -->
    <div>
      <div class="mb-1.5 flex items-center justify-between">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Durée (jours) *</label>
        <label class="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          <input type="checkbox" formControlName="durationUnlimited" (change)="syncUnlimited()"
            class="size-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/30 dark:border-gray-600 dark:bg-gray-900" />
          Illimitée
        </label>
      </div>
      <input type="number" step="1" min="1" formControlName="durationDays"
        [class.opacity-50]="editForm.controls.durationUnlimited.value"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
    </div>

    <!-- Plafond prestataires -->
    <div>
      <div class="mb-1.5 flex items-center justify-between">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Prestataires max *</label>
        <label class="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          <input type="checkbox" formControlName="maxUnlimited" (change)="syncUnlimited()"
            class="size-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/30 dark:border-gray-600 dark:bg-gray-900" />
          Illimité
        </label>
      </div>
      <input type="number" step="1" min="1" formControlName="maxPrestataires"
        [class.opacity-50]="editForm.controls.maxUnlimited.value"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Nombre de prestataires que l'opérateur peut recruter avec ce palier.</p>
    </div>

    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
      <textarea rows="3" formControlName="description" placeholder="Optionnel — affiché aux opérateurs lors de l'achat"
        class="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"></textarea>
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeEdit()" [disabled]="editSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="editSaving() || editForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (editSaving()) { Enregistrement… } @else { Enregistrer }
      </button>
    </div>
  </form>
</app-modal>
